import { Toolbar, ToolbarTab } from 'flexmonster';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Pivot } from 'react-flexmonster';
import { useIntl } from 'react-intl';

import fontUrl from '../../assets/NotoSans-Regular.ttf';

import locUa from './localizations/uk.json';
import { messages } from './messages';
import PivotSelectOrientationForm from './PivotSelectOrientationForm';
import type { PivotReportProps } from './types';
import 'flexmonster/flexmonster.css';

export type PageOrientation = 'landscape' | 'portrait';

const PivotReport = ({
  licenseKey,
  reportRef,
  show,
  currentPreset,
  isDarkMode,
  onOpenPreset,
  onSavePreset,
  onReportChange,
  onOrientationSelectorChange,
}: PivotReportProps): React.JSX.Element | null => {
  const global = { localization: locUa };
  const { formatMessage } = useIntl();
  const [openOrientationSelector, setOpenOrientationSelector] = useState(false);

  useEffect(() => {
    const loadTheme = () => {
      const darkTheme = document.querySelector(
        '[data-flexmonster-theme="dark"]'
      ) as HTMLLinkElement | null;

      if (isDarkMode) {
        if (!darkTheme) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = '/flexmonster-dark.css';
          link.dataset.flexmonsterTheme = 'dark';
          document.head.appendChild(link);
        }
      } else if (darkTheme) {
        darkTheme.remove();
      }
    };

    loadTheme();
  }, [isDarkMode]);

  const handlerMap = useMemo(
    () => ({
      open: onOpenPreset,
      save: () => onSavePreset(currentPreset),
    }),
    [currentPreset, onOpenPreset, onSavePreset]
  );

  const buildPivotToolbarTab = useCallback(
    (name: 'open' | 'save', toolbar: Toolbar): ToolbarTab => ({
      id: `fm-tab-preset-${name}`,
      title: formatMessage(messages[`${name}Preset` as const]),
      icon: toolbar.icons[name],
      handler: handlerMap[name],
    }),
    [formatMessage, handlerMap]
  );

  const handleSelectOrientation = useCallback(
    (pageOrientation: PageOrientation) => {
      setOpenOrientationSelector(false);
      onOrientationSelectorChange(false);

      reportRef.current?.flexmonster.exportTo('pdf', {
        fontUrl,
        pageOrientation,
      });
    },
    [onOrientationSelectorChange, reportRef]
  );

  const customizeToolbar = useCallback(
    (toolbar: Toolbar): void => {
      const tabs = toolbar.getTabs();
      toolbar.getTabs = () => {
        const exportToPdfTab = tabs
          .find(tab => tab.id === 'fm-tab-export')
          ?.menu?.find(tab => tab.id === 'fm-tab-export-pdf');
        if (exportToPdfTab) {
          exportToPdfTab.handler = () => {
            if (document.fullscreenElement) {
              void document.exitFullscreen();
            }
            setOpenOrientationSelector(true);
            onOrientationSelectorChange(true);
          };
        }
        tabs.push(
          buildPivotToolbarTab('open', toolbar),
          buildPivotToolbarTab('save', toolbar),
          {
            id: 'fm-tab-expand',
            title: locUa.contextMenu.expand,
            icon: toolbar.icons.fullscreen,
            handler: () => reportRef.current?.flexmonster.expandAllData(),
          },
          {
            id: 'fm-tab-collapse',
            title: locUa.contextMenu.collapse,
            icon: toolbar.icons.minimize,
            handler: () => reportRef.current?.flexmonster.collapseAllData(),
          }
        );
        return tabs.filter(
          tab => !['fm-tab-open', 'fm-tab-connect'].includes(tab.id)
        );
      };
    },
    [buildPivotToolbarTab, onOrientationSelectorChange, reportRef]
  );

  if (!show || !licenseKey) {
    return null;
  }

  return (
    <>
      <Pivot
        ref={reportRef}
        toolbar={true}
        width="99%"
        height="100%"
        global={global}
        beforetoolbarcreated={customizeToolbar}
        licenseKey={licenseKey}
        reportchange={onReportChange}
      />
      <PivotSelectOrientationForm
        open={openOrientationSelector}
        title={formatMessage(messages.orientationSelectorTitle)}
        onSelect={handleSelectOrientation}
        onCancel={() => {
          setOpenOrientationSelector(false);
          onOrientationSelectorChange(false);
        }}
      />
    </>
  );
};

export default memo(PivotReport);
